import { isInlineToolCall } from './tools/inline';

import type { AssistantAction, Message, StreamFrame } from './types';

const GENERIC_ERROR = 'Something went wrong - please try again.';

const replaceLast = (messages: Message[], message: Message): Message[] => [...messages.slice(0, -1), message];

export function updateActionStatuses(messages: Message[], updates: AssistantAction[]): Message[] {
  if (!updates.length) return messages;

  const byId = new Map(updates.map(update => [update.id, update]));
  return messages.map(message => (
    message.actions?.some(action => byId.has(action.id))
      ? { ...message, actions: message.actions.map(action => byId.get(action.id) ?? action) }
      : message
  ));
}

// Applies a stream frame to the message list, mutating the trailing assistant placeholder
// that `sendMessage` appended on submit. Pure so it can be unit-tested and used inside a
// functional `setMessages` update.
//
// `done.content` is authoritative: halting tools (Navigate/Support) end the turn without
// streaming any deltas, so their reply exists only on the terminal frame. Inline tool calls
// are transcript content and land on the message; the rest are side effects the caller runs.
export function applyStreamFrame(messages: Message[], frame: StreamFrame): Message[] {
  const last = messages[messages.length - 1];
  if (!last) return messages;

  switch (frame.type) {
  case 'status':
    return replaceLast(messages, { ...last, status: frame.label });
  case 'steps':
    return replaceLast(messages, { ...last, steps: frame.steps });
  case 'step_done':
    return replaceLast(messages, {
      ...last,
      steps: last.steps?.map(step => (step.key === frame.key ? { ...step, done: true } : step)),
    });
  case 'delta':
    return replaceLast(messages, { ...last, content: last.content + frame.text, status: undefined });
  case 'tool_calls':
    return replaceLast(messages, { ...last, inlineToolCalls: frame.toolCalls.filter(isInlineToolCall) });
  case 'done': {
    const updated = updateActionStatuses(messages, frame.actions ?? []);
    const known = new Set<number>();
    messages.forEach(message => message.actions?.forEach(action => known.add(action.id)));
    const fresh = (frame.actions ?? []).filter(action => action.status === 'pending' && !known.has(action.id));
    const trailing = updated[updated.length - 1];

    return replaceLast(updated, {
      ...trailing,
      content: frame.content,
      inlineToolCalls: frame.toolCalls?.filter(isInlineToolCall),
      metrics: frame.metrics,
      actions: fresh.length ? [...(trailing.actions ?? []), ...fresh] : trailing.actions,
      isStreaming: false,
      status: undefined,
    });
  }
  case 'error':
    return replaceLast(messages, {
      ...last,
      content: last.content || GENERIC_ERROR,
      isStreaming: false,
      status: undefined,
      error: true,
    });
  default:
    return messages;
  }
}
